import {
  ApiResponse,
  ApiError,
  RequestConfig,
  RechargeConfigData,
  GetRechargeConfigResponse,
  GenerateLinkRequest,
  GenerateLinkData,
  GenerateLinkResponse,
  EnvironmentConfig
} from './types';

// 默认配置
const DEFAULT_CONFIG: EnvironmentConfig = {
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:3000/api',
  timeout: 10000,
  retryConfig: {
    maxRetries: 3,
    retryDelay: 1000,
    backoffMultiplier: 2
  },
  enableCache: true,
  cacheConfig: {
    defaultTtl: 5 * 60 * 1000 // 5分钟
  }
};

// 自定义错误类
class ApiClientError extends Error {
  public readonly errorCode: number;
  public readonly details?: any;

  constructor(message: string, errorCode: number, details?: any) {
    super(message);
    this.name = 'ApiClientError';
    this.errorCode = errorCode;
    this.details = details;
  }
}

// 网络错误类
class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

// 超时错误类
class TimeoutError extends Error {
  constructor(message: string = '请求超时') {
    super(message);
    this.name = 'TimeoutError';
  }
}

// 请求拦截器类型
type RequestInterceptor = (config: RequestConfig & { url: string }) => RequestConfig & { url: string };

// 响应拦截器类型
type ResponseInterceptor = <T>(response: ApiResponse<T>) => ApiResponse<T>;

// 错误拦截器类型
type ErrorInterceptor = (error: ApiError) => ApiError;

class ApiClient {
  private config: EnvironmentConfig;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  constructor(config?: Partial<EnvironmentConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // 添加请求拦截器
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  // 添加响应拦截器
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  // 添加错误拦截器
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  // 睡眠函数
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 创建超时控制器
  private createTimeoutController(timeout: number): AbortController {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeout);
    return controller;
  }

  // 应用请求拦截器
  private applyRequestInterceptors(config: RequestConfig & { url: string }): RequestConfig & { url: string } {
    return this.requestInterceptors.reduce((acc, interceptor) => interceptor(acc), config);
  }

  // 应用响应拦截器
  private applyResponseInterceptors<T>(response: ApiResponse<T>): ApiResponse<T> {
    return this.responseInterceptors.reduce((acc, interceptor) => interceptor(acc), response);
  }

  // 应用错误拦截器
  private applyErrorInterceptors(error: ApiError): ApiError {
    return this.errorInterceptors.reduce((acc, interceptor) => interceptor(acc), error);
  }

  // 处理响应
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let data: any;
    
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      throw new ApiClientError('响应解析失败', -1, { originalError: error });
    }

    // 检查业务错误
    if (data.error_code && data.error_code !== 0) {
      const apiError: ApiError = {
        error_code: data.error_code,
        message: data.message || '未知错误',
        details: data
      };
      
      const processedError = this.applyErrorInterceptors(apiError);
      throw new ApiClientError(processedError.message, processedError.error_code, processedError.details);
    }

    // 检查 HTTP 状态码
    if (!response.ok) {
      const apiError: ApiError = {
        error_code: response.status,
        message: data.message || `HTTP ${response.status}: ${response.statusText}`,
        details: data
      };
      
      const processedError = this.applyErrorInterceptors(apiError);
      throw new ApiClientError(processedError.message, processedError.error_code, processedError.details);
    }

    const apiResponse: ApiResponse<T> = {
      data: data.data,
      error_code: data.error_code || 0,
      message: data.message || ''
    };

    return this.applyResponseInterceptors(apiResponse);
  }

  // 执行请求（带重试）
  private async executeRequest<T>(
    url: string,
    config: RequestConfig,
    retryCount: number = 0
  ): Promise<ApiResponse<T>> {
    const { timeout = this.config.timeout } = config;
    const controller = this.createTimeoutController(timeout);

    try {
      const requestConfig = this.applyRequestInterceptors({ ...config, url });
      
      const fetchOptions: RequestInit = {
        method: requestConfig.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...requestConfig.headers
        },
        signal: controller.signal
      };

      if (requestConfig.body && requestConfig.method !== 'GET') {
        fetchOptions.body = typeof requestConfig.body === 'string' 
          ? requestConfig.body 
          : JSON.stringify(requestConfig.body);
      }

      const response = await fetch(requestConfig.url, fetchOptions);
      return await this.handleResponse<T>(response);

    } catch (error) {
      // 处理超时错误
      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`请求超时 (${timeout}ms)`);
      }

      // 处理网络错误
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError('网络连接失败');
      }

      // 如果是 ApiClientError，直接抛出
      if (error instanceof ApiClientError) {
        throw error;
      }

      // 重试逻辑
      const { maxRetries, retryDelay, backoffMultiplier = 2 } = this.config.retryConfig;
      
      if (retryCount < maxRetries) {
        const delay = retryDelay * Math.pow(backoffMultiplier, retryCount);
        console.warn(`请求失败，${delay}ms 后进行第 ${retryCount + 1} 次重试:`, error);
        
        await this.sleep(delay);
        return this.executeRequest<T>(url, config, retryCount + 1);
      }

      // 重试次数用完，抛出最终错误
      throw error instanceof Error 
        ? error 
        : new ApiClientError('未知错误', -1, error);
    }
  }

  // 通用请求方法
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.config.apiBaseUrl}${endpoint}`;
    return this.executeRequest<T>(url, config);
  }

  // GET 请求
  async get<T>(endpoint: string, config: Omit<RequestConfig, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  // POST 请求
  async post<T>(endpoint: string, data?: any, config: Omit<RequestConfig, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body: data });
  }

  // PUT 请求
  async put<T>(endpoint: string, data?: any, config: Omit<RequestConfig, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body: data });
  }

  // DELETE 请求
  async delete<T>(endpoint: string, config: Omit<RequestConfig, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  // 更新配置
  updateConfig(newConfig: Partial<EnvironmentConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // 获取当前配置
  getConfig(): EnvironmentConfig {
    return { ...this.config };
  }
}

// 创建默认实例
export const apiClient = new ApiClient();

// 添加默认请求拦截器（添加时间戳等）
apiClient.addRequestInterceptor((config) => {
  // 添加时间戳防止缓存
  const url = new URL(config.url);
  url.searchParams.set('_t', Date.now().toString());
  
  return {
    ...config,
    url: url.toString(),
    headers: {
      ...config.headers,
    }
  };
});

// 添加默认响应拦截器（日志记录等）
apiClient.addResponseInterceptor((response) => {
  if (import.meta.env.DEV) {
    console.log('API Response:', response);
  }
  return response;
});

// 添加默认错误拦截器（错误日志等）
apiClient.addErrorInterceptor((error) => {
  if (import.meta.env.DEV) {
    console.error('API Error:', error);
  }
  return error;
});

// 具体的 API 方法
export const rechargeApi = {
  // 获取充值配置
  async getRechargeConfigs(): Promise<GetRechargeConfigResponse> {
    return apiClient.get<RechargeConfigData>('/preview_recharge/recharge_configs');
  },

  // 生成支付链接
  async generatePaymentLink(params: GenerateLinkRequest): Promise<GenerateLinkResponse> {
    return apiClient.post<GenerateLinkData>('/preview_recharge/generate_link', params);
  }
};

// 导出类型和实例
export { ApiClient, ApiClientError, NetworkError, TimeoutError };
export default apiClient;